import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import config from '../config'
import { compareDesc } from 'date-fns'

Vue.use(Vuex)

const initialState = {
  communities: [],
  loading: false,
  filteredCommunities: [],
  communityDetail: getDefaultCommunityDetails(),
  communitiesInfos: {},
  communityFilter: '',
  communitiesSort: ''
}

const getters = {
  communities: state => state.communities,
  filteredCommunities: state => state.filteredCommunities,
  communityDetail: state => state.communityDetail,
  communitiesInfos: state => state.communitiesInfos,
  communityFilter: state => state.communityFilter,
  communitiesSort: state => state.communitiesSort,
  suggestedCommunities: state => state.suggestedCommunities
}

const mutations = {
  communityDetail: (state, communityDetail) => {
    state.communityDetail = communityDetail
  },
  communities: (state, communities) => {
    state.communities = communities
  },
  filteredCommunities: (state, filteredCommunities) => {
    state.filteredCommunities = filteredCommunities
  },
  communitiesInfos: (state, communitiesInfos) => {
    state.communitiesInfos = communitiesInfos
  },
  setCommunityFilter: (state, communityFilter) => {
    state.communityFilter = communityFilter
  },
  addCommunity: (state, community) => {
    state.communities.push(community)
  },
  setCommunitiesSort: (state, communitiesSort) => {
    state.communitiesSort = communitiesSort
  },
  suggestedCommunities: (state, suggestedCommunities) => {
    state.suggestedCommunities = suggestedCommunities
  },
  incrementUserCount: (state) => {
    state.communityDetail.countUsers = state.communityDetail.countUsers + 1
  },
  decrementUserCount: (state) => {
    state.communityDetail.countUsers = state.communityDetail.countUsers - 1
  },
  incrementPostCount: (state) => {
    state.communityDetail.countPosts = state.communityDetail.countPosts + 1
    state.communitiesInfos.countPosts = state.communitiesInfos.countPosts + 1
  },
  decrementPostCount: (state) => {
    state.communityDetail.countPosts = state.communityDetail.countPosts - 1
    state.communitiesInfos.countPosts = state.communitiesInfos.countPosts - 1
  },
  updateCommunity: (state, community) => {
    // Update in communities list
    const communities = state.communities
    const index = communities.findIndex(c => c.id === community.id)
    if (index !== -1) {
      state.communities[index] = community
    }
    // Update in detail
    if (state.communityDetail.community.id === community.id) {
      state.communityDetail.community = community
    }
  },
  addMember: (state, user) => {
    state.communityDetail.members.push(user)
    state.communityDetail.members = sortByUserName(state.communityDetail.members)
  },
  deductMember: (state, user) => {
    const index = state.communityDetail.members.findIndex(m => m.id === user.id)
    state.communityDetail.members.splice(index, 1)
  },
  grantUserForBroadcast: (state, userId) => {
    const index = state.communityDetail.members.findIndex(c => c.id === userId)
    Vue.set(state.communityDetail.members[index], 'communities.UserCommunity.isAllowedToPost', true)
  },
  revokeUserForBroadcast: (state, userId) => {
    const index = state.communityDetail.members.findIndex(c => c.id === userId)
    Vue.set(state.communityDetail.members[index], 'communities.UserCommunity.isAllowedToPost', false)
  },
  communityDocuments: (state, documents) => {
    Vue.set(state.communityDetail, 'documents', documents)
  },
  loading: (state, loading) => {
    state.loading = loading
  }
}

const loadCommunities = async (cursors, commit, state, dispatch) => {
  const response = await axios.get(`${config.getRootApi()}/communities/`, {
    params: {
      size: 25,
      after: cursors.after
    }
  })
  commit('communities', state.communities.concat(response.data.results))
  await dispatch('filterCommunities', state.communityFilter)
  await dispatch('sortCommunities', state.communitiesSort)
  if (response.data.cursors.hasNext) {
    await loadCommunities(response.data.cursors, commit, state, dispatch)
  }
}

const actions = {
  async fetchCommunities({ commit, state, dispatch }) {
    if (!state.loading) {
      commit('communities', [])
      commit('loading', true)
      try {
        await loadCommunities({}, commit, state, dispatch)
      } finally {
        commit('loading', false)
      }
    }
  },
  async fetchCommunityDetails({ commit }, id) {
    commit('communityDetail', getDefaultCommunityDetails())
    return axios.get(`${config.getRootApi()}/communities/${id}/owner`)
      .then(async (result) => {
        if (result.status === 200) {
          let communityDetail = result.data
          const responseCountMembers = await axios.get(`${config.getRootApi()}/communities/${id}/countUsers`)
          const responseCountPost = await axios.get(`${config.getRootApi()}/communities/${id}/countPosts`)
          const responseMembers = await axios.get(`${config.getRootApi()}/communities/${id}/members`)
          const responseDocuments = await axios.get(`${config.getRootApi()}/communities/${id}/documents`)
          communityDetail.countPosts = responseCountPost.data ? responseCountPost.data.count : 0
          communityDetail.countUsers = responseCountMembers.data.count
          communityDetail.members = responseMembers.data
          communityDetail.documents = responseDocuments.data
          commit('communityDetail', communityDetail)
          return true
        } else {
          return false
        }
      })
      .catch(() => {
        return false
      })
  },
  async fetchCommunityDocuments({ commit }, idCommunity) {
    const responseDocuments = await axios.get(`${config.getRootApi()}/communities/${idCommunity}/documents`)
    commit('communityDocuments', responseDocuments.data)
  },
  async fetchCommunity({ commit }, id) {
    const response = await axios.get(`${config.getRootApi()}/communities/${id}`)
    commit('updateCommunity', response.data)
  },
  createCommunity({ dispatch }, community) {
    let params = {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }
    return axios.post(`${config.getRootApi()}/communities`, community, params)
      .then(result => {
        if (result.status === 201) {
          dispatch('fetchUserCommunities')
        }
        return result
      })
      .catch((err) => {
        return err
      })
  },
  updateCommunity({ dispatch }, payload) {
    return axios.put(`${config.getRootApi()}/communities/${payload.communityId}`, payload.formData)
      .then(result => {
        if (result.status === 200) {
          dispatch('fetchCommunity', payload.communityId)
        }
        return result
      })
      .catch((err) => {
        return err
      })
  },
  async joinCommunity({ commit, dispatch, rootState }, idCommunity) {
    const result = await axios.post(`${config.getRootApi()}/communities/${idCommunity}/join`, null)
    if (result.status === 200) {
      await dispatch('fetchUserCommunities')
      commit('incrementUserCount')
      commit('addMember', rootState.user.user)
    }
    return result
  },
  async leaveCommunity({ commit, dispatch, rootState }, idCommunity) {
    const result = await axios.post(`${config.getRootApi()}/communities/${idCommunity}/leave`, null)
    if (result.status === 200) {
      await dispatch('fetchUserCommunities')
      commit('decrementUserCount')
      commit('deductMember', rootState.user.user)
    }
  },
  async reportCommunity({ commit }, id) {
    const response = await axios.post(`${config.getRootApi()}/communities/${id}/reportCommunity`, null)
    return response.status === 200
  },
  async fetchCommunitiesInfo({ commit }) {
    const responseCommunitiesInfo = await axios.get(`${config.getRootApi()}/communities/communitiesInfo`)
    if (responseCommunitiesInfo.status === 200) {
      commit('communitiesInfos', responseCommunitiesInfo.data)
    }
  },
  filterCommunities({ dispatch, state, rootState, commit }, filter) {
    let communities = []
    commit('setCommunityFilter', filter)
    let userId = rootState.user.user.id
    let userCommunities = rootState.user.userCommunities.map(userCommunity => userCommunity.idCommunity)
    switch (filter) {
      case 'all':
        communities = state.communities
        break
      case 'myCommunities':
        communities = state.communities.filter(community => userCommunities.includes(community.id))
        break
      case 'myCreatedCommunities':
        communities = state.communities.filter(community => {
          return community.owner && community.owner.id === userId && userCommunities.includes(community.id)
        })
        break
      case 'myJoinedCommunities':
        communities = state.communities.filter(community => {
          return community.owner && community.owner.id !== userId && userCommunities.includes(community.id)
        })
        break
    }
    commit('filteredCommunities', communities)
    dispatch('sortCommunities', state.communitiesSort)
  },
  async addCommunity({ commit, rootState }, community) {
    community.owner = rootState.user.user
    commit('addCommunity', community)
  },
  async sortCommunities({ commit, state }, sortBy) {
    commit('setCommunitiesSort', sortBy)
    if (sortBy === 'popularity') {
      await commit('filteredCommunities', sortByPopularity([...state.filteredCommunities]))
    } else if (sortBy === 'alphabetical') {
      await commit('filteredCommunities', sortByAlphabeticalOrder([...state.filteredCommunities]))
    } else {
      await commit('filteredCommunities', sortByChronology([...state.filteredCommunities]))
    }
  },
  resetSortCommunities({ commit }) {
    commit('setCommunitiesSort', 'chronology')
  },
  async suggestedCommunities({ commit }) {
    return axios.get(`${config.getRootApi()}/communities/suggested`)
      .then(response => {
        commit('suggestedCommunities', shuffleArray(response.data).slice(0, 3))
        return shuffleArray(response.data).slice(0, 3)
      })

    function shuffleArray(array) {
      for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const temp = array[i]
        array[i] = array[j]
        array[j] = temp
      }
      return array
    }
  },
  async grantUserForBroadcast({ commit }, { communityId, userId }) {
    const result = await axios.post(`${config.getRootApi()}/communities/${communityId}/users/${userId}/setBroadcaster`)
    if (result.status === 200) {
      commit('grantUserForBroadcast', userId)
    }
  },
  async revokeUserForBroadcast({ commit }, { communityId, userId }) {
    const result = await axios.post(`${config.getRootApi()}/communities/${communityId}/users/${userId}/unsetBroadcaster`)
    if (result.status === 200) {
      commit('revokeUserForBroadcast', userId)
    }
  },
  async removeMember({ commit }, { communityId, userId }) {
    const result = await axios.delete(`${config.getRootApi()}/communities/${communityId}/users/${userId}`)
    if (result.status === 200) {
      commit('deductMember', { id: userId })
    }
  },
  resetSuggestedCommunities({ commit }) {
    commit('suggestedCommunities', [])
  }
}

const sortByPopularity = (list) => {
  return list.sort((a, b) => {
    return a && b && (parseInt(b.likesCount) + parseInt(b.postsCount) + parseInt(b.usersCount)) - (parseInt(a.likesCount) + parseInt(a.postsCount) + parseInt(a.usersCount))
  })
}

const sortByChronology = (list) => {
  return list.sort((a, b) => {
    return a && b && compareDesc(a.createdAt, b.createdAt)
  })
}

const sortByAlphabeticalOrder = (list) => {
  return list.sort((a, b) => {
    return a.name.localeCompare(b.name)
  })
}

const sortByUserName = (members) => {
  return members.sort((a, b) => {
    let lastNameOfA = a.lastName
    let lastNameOfB = b.lastName

    if (!lastNameOfA) {
      lastNameOfA = ''
    }
    if (!lastNameOfB) {
      lastNameOfB = ''
    }
    return lastNameOfA.localeCompare(lastNameOfB)
  })
}

function getDefaultCommunityDetails() {
  return { members: [], community: { type: '' } }
}

export default {
  state: initialState,
  getters,
  mutations,
  actions
}
